import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AccordionSummaryProps,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CommonDialog from "../../components/Dialog/CommonDialog";
import {
  useGetInstanceByIdQuery,
} from "../../redux/api/instanceApi";
import { ComponentSize } from "../../utils/enum/enum";
import InstanceDataRow from "../../components/InstanceDetails/InstanceDataRow";
import InstanceContactDataTable from "../../components/InstanceDetails/InstanceContactDataTable";
import InstanceAdminDataTable from "../../components/InstanceDetails/InstanceAdminDataTable";
import InstanceReadersTable from "../../components/InstanceDetails/InstanceReadersTable";
import InstanceTagsTable from "../../components/InstanceDetails/InstanceTagsTable";
import InstanceEncodingTable from "../../components/InstanceDetails/InstanceEncodingTable";
import { transformServiceLevelToString } from "../../utils/functions/functions";

type InstanceDetailDialogueType = {
  isOpen: boolean;
  handleClose: () => void;
  instanceId: string;
};

const StyledWrapper = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  fontFamily: theme.typography.fontFamily
}));

const CustomAccordionSummary = styled(AccordionSummary)<AccordionSummaryProps>(() => ({
  fontSize: "150%"
}));

function InstanceDetails ({
  instanceId,
  handleClose,
  isOpen
}: InstanceDetailDialogueType) {
  const [expandedAccordions, setExpandedAccordions] = useState(["summary"]);
  const { data: instance } = useGetInstanceByIdQuery(instanceId, {
    refetchOnMountOrArgChange: true,
  });

  // fallback to ensure that data later on is valid
  if (!instance) {
    return (
      <CommonDialog isOpen={isOpen} onClose={handleClose} title="Invalid instance">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            Invalid Data
          </Grid>
        </Grid>
      </CommonDialog>
    )
  };

  // accordion controls
  const collapseAllAccordions = () => {
    setExpandedAccordions([]);
  };
  const expandAllAccordions = () => {
    setExpandedAccordions(
      [
        'summary',
        'contacts',
        'limits',
        'information',
        'readers',
        'code',
        'encodings',
      ]
    );
  };
  const accordionClicked = (key: string) => {
    if (expandedAccordions.includes(key))
      setExpandedAccordions(
        expandedAccordions.filter((entry) => entry !== key)
      );
    else setExpandedAccordions([...expandedAccordions, key]);
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  // new we can be more confident that data is actually valid
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      onClick={handleDialogClick}
      aria-labelledby="responsive-dialog-title"
      maxWidth={ComponentSize.Xl}
      scroll="paper"
    >
      <DialogTitle
          sx={{
            m: 0,
            p: 2,
            position: "relative",
          }}
        >
          {instance.name} instance information
          <Button
            onClick={expandAllAccordions}
            sx={{
              m: 0,
              p: 2,
              position: "absolute",
              right: 240,
              top: 8,
            }}
          >
            Expand all
          </Button>
          <Button
            onClick={collapseAllAccordions}
            sx={{
              m: 0,
              p: 2,
              position: "absolute",
              right: 100,
              top: 8,
            }}
          >
            Collapse all
          </Button>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <StyledWrapper>
            <Grid item xs={12}>
              <Accordion
                id="summary"
                onChange={() => accordionClicked("summary")}
                expanded={expandedAccordions.includes("summary")}
              >
                <CustomAccordionSummary>
                  Instance summary
                </CustomAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {InstanceDataRow('Name', instance.name)}
                    {InstanceDataRow('Subdomain', instance.subdomain)}
                    {InstanceDataRow('Created', instance.created_at, "time")}
                    {InstanceDataRow('Updated', instance.updated_at, "time")}
                    {InstanceDataRow('Launched', instance.launched_at, "time")}
                    {InstanceDataRow('Version updated', instance.version_updated_at, "time")}
                    {InstanceDataRow('Service level', transformServiceLevelToString(instance.service_level))}
                  </Grid>
                  <Divider sx={{ m: 2 }} />
                  <Grid container spacing={1}>
                    {InstanceDataRow('URL (frontend)', instance.frontend_url, "url")}
                    {InstanceDataRow('URL (backend)', instance.backend_url, "url")}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion
                id="contacts"
                onChange={() => accordionClicked("contacts")}
                expanded={expandedAccordions.includes("contacts")}
              >
                <CustomAccordionSummary>
                  Contact details
                </CustomAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {InstanceDataRow('Creator Username', instance.creator)}
                    {InstanceDataRow('Contact Person Email', instance.subscription_metadata.contactPerson)}
                    {InstanceAdminDataTable(instance.parameters)}
                    {InstanceContactDataTable("sales", instance.subscription_metadata)}
                    {InstanceContactDataTable("resellers", instance.subscription_metadata)}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion
                id="limits"  
                onChange={() => accordionClicked("limits")}
                expanded={expandedAccordions.includes("limits")}
              >
                <CustomAccordionSummary>
                  Subscription limits
                </CustomAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {InstanceDataRow('Reader limit', instance.subscription_reader_limit)}
                    {InstanceDataRow('Leaf location limit', instance.subscription_reader_limit)}
                    {InstanceDataRow('Expiration date', instance.subscription_valid_until, "time")}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion
                id="information"
                onChange={() => accordionClicked("information")}
                expanded={expandedAccordions.includes("information")}
              >
                <CustomAccordionSummary>
                  Information
                </CustomAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {InstanceDataRow('Total number of locations', instance.location_total)}
                    {InstanceDataRow('Leaf locations', instance.location_leafs)}
                    {InstanceDataRow('Active leaf locations', `${instance.location_active_leafs}/${instance.subscription_leaf_location_limit}`)}
                    {InstanceDataRow('Number of products', instance.product_count)}
                    {InstanceDataRow('Number of tags', instance.tag_count)}
                    {InstanceDataRow('Monthly process list count', instance.monthly_process_list_count)}
                    {InstanceDataRow('Last received process list', instance.last_received_list, "time")}
                    {InstanceDataRow('Last processed process list', instance.last_processed_list, "time")}
                    {InstanceDataRow('Last login', instance.last_login, "time")}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion
                id="readers"
                onChange={() => accordionClicked("readers")}
                expanded={expandedAccordions.includes("readers")}
              >
                <CustomAccordionSummary>
                  Reader Status
                </CustomAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {InstanceReadersTable(instance.instance_readers)}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion
                id="code"
                onChange={() => accordionClicked("code")}
                expanded={expandedAccordions.includes("code")}
              >
                <CustomAccordionSummary>
                  Versions and Extensions
                </CustomAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {InstanceTagsTable(instance.deployed_instance_tags, "Deployed")}
                    {InstanceTagsTable(instance.assigned_instance_tags, "Assigned")}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion
                id="encodings"
                onChange={() => accordionClicked("encodings")}
                expanded={expandedAccordions.includes("encodings")}
              >
                <CustomAccordionSummary>
                  Encodings
                </CustomAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {InstanceEncodingTable(instance.encodings)}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </StyledWrapper>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default InstanceDetails;